/**
 * @module ConsentsService
 * @remarks RTK Query service for fetching customer consents data
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "keep-react";
import { Consent, ConsentAddResponse, AddConsentRequest } from "@models/consent.model";
import i18next from "i18next";

/**
 * API service für Kundendaten-Einwilligungen
 */
export const consentsApi = createApi({
  reducerPath: "consentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Consents"],
  endpoints: (builder) => ({
    getConsents: builder.mutation<Consent[], string>({
      query: (customer_id) => ({
        url: `data/einwilligungen`,
        method: "POST",
        body: {
          where: {
            kunde: customer_id
          },
          fields: {}
        },
      }),
      // Transform response if needed
      transformResponse: (response: Consent[]) => {
        return response;
      },
      // Handle success and error with toast notifications
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          console.error(err);
          toast.error(i18next.t("api.consents.fetchError"));
        }
      },
      invalidatesTags: ["Consents"],
    }),
    addConsent: builder.mutation<ConsentAddResponse[], AddConsentRequest>({
      query: ({ customer_id, theme = 3, opt = 1 }) => ({
        url: `data/einwilligungen`,
        method: "PUT",
        body: {
          fields: {
            kunde: customer_id,
            thema: theme,
            datum_einwilligung: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD
            opt: opt
          }
        },
      }),
      // Transform response if needed
      transformResponse: (response: ConsentAddResponse[]) => {
        return response;
      },
      // Handle success and error with toast notifications
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(i18next.t("api.consents.saveSuccess"));
        } catch (err) {
          console.error(err);
          toast.error(i18next.t("api.consents.saveError"));
        }
      },
      invalidatesTags: ["Consents"],
    }),
  }),
});

// Export hooks for use in components
export const { useGetConsentsMutation, useAddConsentMutation } = consentsApi;
